import { createFileRoute, Link } from '@tanstack/react-router'
import { buildSeo } from './-lib/seo'
import { createSiteTranslate } from './-lib/site-i18n'

export const Route = createFileRoute('/(public)/terms')({
	ssr: true,
	head: ({ match }) => {
		const { appLanguage } = match.context
		const t = createSiteTranslate(appLanguage)
		return buildSeo({
			appLanguage,
			path: '/terms',
			title: t('site:seo.terms.title'),
			description: t('site:seo.terms.description')
		})
	},
	component: TermsPage
})

function SectionHeading(props: { num: string; title: string }) {
	return (
		<h2>
			<span className='num'>{props.num}</span>
			{props.title}
		</h2>
	)
}

function TermsPage() {
	const { appLanguage } = Route.useRouteContext()
	const t = createSiteTranslate(appLanguage)

	return (
		<>
			<section className='doc-hero'>
				<div className='wrap'>
					<span className='eyebrow'>
						{t('site:terms.hero.eyebrow')}
					</span>
					<h1>{t('site:terms.hero.title')}</h1>
					<p>{t('site:terms.hero.subtitle')}</p>
					<div className='doc-updated'>
						{t('site:terms.hero.effective')}
					</div>
				</div>
			</section>

			<div className='wrap doc-body'>
				<aside className='doc-toc'>
					<h4>{t('site:terms.toc.heading')}</h4>
					<a href='#s1'>{t('site:terms.toc.s1')}</a>
					<a href='#s2'>{t('site:terms.toc.s2')}</a>
					<a href='#s3'>{t('site:terms.toc.s3')}</a>
					<a href='#s4'>{t('site:terms.toc.s4')}</a>
					<a href='#s5'>{t('site:terms.toc.s5')}</a>
					<a href='#s6'>{t('site:terms.toc.s6')}</a>
					<a href='#s7'>{t('site:terms.toc.s7')}</a>
					<a href='#s8'>{t('site:terms.toc.s8')}</a>
					<a href='#s9'>{t('site:terms.toc.s9')}</a>
					<a href='#s10'>{t('site:terms.toc.s10')}</a>
					<a href='#s11'>{t('site:terms.toc.s11')}</a>
					<a href='#s12'>{t('site:terms.toc.s12')}</a>
				</aside>

				<div className='doc-content'>
					<div className='callout warn'>
						<strong>{t('site:terms.callout.label')}</strong>
						{t('site:terms.callout.pre_placeholder')}
						<em>{t('site:terms.callout.placeholder_word')}</em>
						{t('site:terms.callout.mid_emphasis')}
						<strong>{t('site:terms.callout.no_legal')}</strong>
						{t('site:terms.callout.post_emphasis')}
					</div>

					<section id='s1'>
						<SectionHeading
							num='1'
							title={t('site:terms.s1.title')}
						/>
						<p>{t('site:terms.s1.body')}</p>
					</section>

					<section id='s2'>
						<SectionHeading
							num='2'
							title={t('site:terms.s2.title')}
						/>
						<p>{t('site:terms.s2.body')}</p>
					</section>

					<section id='s3'>
						<SectionHeading
							num='3'
							title={t('site:terms.s3.title')}
						/>
						<p>{t('site:terms.s3.body')}</p>
						<ul>
							<li>{t('site:terms.s3.item1')}</li>
							<li>{t('site:terms.s3.item2')}</li>
							<li>{t('site:terms.s3.item3')}</li>
						</ul>
					</section>

					<section id='s4'>
						<SectionHeading
							num='4'
							title={t('site:terms.s4.title')}
						/>
						<p>{t('site:terms.s4.body')}</p>
						<ul>
							<li>{t('site:terms.s4.item1')}</li>
							<li>{t('site:terms.s4.item2')}</li>
							<li>{t('site:terms.s4.item3')}</li>
						</ul>
					</section>

					<section id='s5'>
						<SectionHeading
							num='5'
							title={t('site:terms.s5.title')}
						/>
						<p>{t('site:terms.s5.body')}</p>
					</section>

					<section id='s6'>
						<SectionHeading
							num='6'
							title={t('site:terms.s6.title')}
						/>
						<p>{t('site:terms.s6.body')}</p>
					</section>

					<section id='s7'>
						<SectionHeading
							num='7'
							title={t('site:terms.s7.title')}
						/>
						<p>{t('site:terms.s7.body')}</p>
					</section>

					<section id='s8'>
						<SectionHeading
							num='8'
							title={t('site:terms.s8.title')}
						/>
						<p>{t('site:terms.s8.body')}</p>
					</section>

					<section id='s9'>
						<SectionHeading
							num='9'
							title={t('site:terms.s9.title')}
						/>
						<p>{t('site:terms.s9.body')}</p>
						<ul>
							<li>{t('site:terms.s9.item1')}</li>
							<li>{t('site:terms.s9.item2')}</li>
							<li>{t('site:terms.s9.item3')}</li>
							<li>{t('site:terms.s9.item4')}</li>
						</ul>
					</section>

					<section id='s10'>
						<SectionHeading
							num='10'
							title={t('site:terms.s10.title')}
						/>
						<p>{t('site:terms.s10.body')}</p>
					</section>

					<section id='s11'>
						<SectionHeading
							num='11'
							title={t('site:terms.s11.title')}
						/>
						<p>{t('site:terms.s11.body')}</p>
					</section>

					<section id='s12'>
						<SectionHeading
							num='12'
							title={t('site:terms.s12.title')}
						/>
						<p>
							{t('site:terms.s12.body_pre')}
							<Link className='inl' to='/support'>
								{t('site:terms.s12.support_link')}
							</Link>
							{t('site:terms.s12.body_mid')}
							<a
								className='inl'
								href='mailto:supporto@sealedger.app'
							>
								supporto@sealedger.app
							</a>
							{t('site:terms.s12.body_post')}
						</p>
					</section>

					<p style={{ color: 'var(--faint)', fontSize: '13.5px' }}>
						{t('site:terms.footer_note.pre')}
						<Link className='inl' to='/privacy'>
							{t('site:terms.footer_note.privacy_link')}
						</Link>
						{t('site:terms.footer_note.post')}
					</p>
				</div>
			</div>
		</>
	)
}
